import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { formatDate } from "../lib/format";

const AdminImportHistory = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/admin/import/history").then((r) => { setRuns(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-import-history">
      <div>
        <Link to="/admin/imports/cartrade" className="text-sm text-[#242424]/70 inline-flex items-center gap-2 mb-2"><ArrowLeft size={14} /> Back to Import</Link>
        <div className="overline text-[#7A1519]">History</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">Import runs</h1>
      </div>

      {loading && <div className="text-sm text-[#242424]/60">Loading…</div>}
      {!loading && runs.length === 0 && (
        <div className="bg-white border hairline p-10 rounded-sm text-center">
          <FileSpreadsheet size={28} className="mx-auto text-[#7A1519]" />
          <div className="mt-3 text-sm text-[#242424]/70">No imports yet.</div>
          <Link to="/admin/imports/cartrade" className="mt-4 inline-flex items-center gap-2 bg-[#7A1519] text-white px-5 py-2.5 rounded-sm text-sm">Start an Import <ArrowRight size={14} /></Link>
        </div>
      )}

      <div className="space-y-3">
        {runs.map((r) => (
          <div key={r.id} className="bg-white border hairline rounded-sm" data-testid={`run-${r.id}`}>
            <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="w-full text-left p-5 flex items-center justify-between gap-3">
              <div>
                <div className="font-medium">{r.file_name}</div>
                <div className="text-xs text-[#242424]/70 mt-1">
                  {formatDate(r.created_at)} · by <span className="font-mono">{r.admin_email}</span> · <span className="uppercase tracking-[0.15em]">{r.source}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-[#7A1519] font-semibold">{r.counts?.new || 0} new</span>
                <span className="text-amber-700 font-semibold">{r.counts?.updated || 0} updated</span>
                <span className="text-[#242424]/60">{r.counts?.unchanged || 0} unchanged</span>
                <span className="text-[#242424]/60">{r.counts?.missing_applied || 0}/{r.counts?.missing_seen || 0} missing</span>
              </div>
            </button>
            {expanded === r.id && (
              <div className="border-t hairline p-5 bg-[#F7F6F3]/40 space-y-4">
                <div className="text-sm">Missing action: <span className="font-semibold capitalize">{(r.missing_action || "leave_unchanged").replace(/_/g, " ")}</span></div>

                {r.applied?.created?.length > 0 && (
                  <Section title={`Created (${r.applied.created.length})`}>
                    {r.applied.created.map((c) => (
                      <ItemRow key={c.external_id} left={c.summary} right={<Link className="text-[#7A1519] hover:underline text-xs" to={`/admin/inventory/${c.vehicle_id}`}>Open →</Link>} idText={c.external_id} />
                    ))}
                  </Section>
                )}
                {r.applied?.updated?.length > 0 && (
                  <Section title={`Updated (${r.applied.updated.length})`}>
                    {r.applied.updated.map((u) => (
                      <div key={u.external_id} className="border hairline bg-white p-3 rounded-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm">{u.summary} <span className="text-xs text-[#242424]/60 font-mono ml-2">#{u.external_id}</span></div>
                          <Link className="text-[#7A1519] hover:underline text-xs" to={`/admin/inventory/${u.vehicle_id}`}>Open →</Link>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          {Object.entries(u.changes || {}).map(([f, ch]) => (
                            <span key={f} className="border hairline px-2 py-1 rounded-sm bg-[#F7F6F3]">
                              <span className="uppercase tracking-[0.15em] text-[#242424]/60 mr-2">{f.replace(/_/g," ")}</span>
                              <span className="line-through text-[#242424]/60 mr-1">{String(ch.before ?? "—")}</span>
                              →
                              <span className="text-[#111111] font-semibold ml-1">{String(ch.after ?? "—")}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </Section>
                )}
                {r.applied?.missing_applied?.length > 0 && (
                  <Section title={`Missing applied (${r.applied.missing_applied.length})`}>
                    {r.applied.missing_applied.map((m) => (
                      <ItemRow key={m.external_id} left={m.summary} right={<span className="text-xs uppercase tracking-[0.15em] text-[#242424]/60">→ {m.new_status}</span>} idText={m.external_id} />
                    ))}
                  </Section>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="overline text-[#7A1519] mb-2">{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const ItemRow = ({ left, right, idText }) => (
  <div className="border hairline bg-white rounded-sm px-3 py-2 flex items-center justify-between gap-2 text-sm">
    <div><span className="mr-2 text-xs font-mono text-[#242424]/60">#{idText}</span>{left}</div>
    <div>{right}</div>
  </div>
);

export default AdminImportHistory;
